/**
 * CNPJ — validação e formatação. FONTE ÚNICA compartilhada entre o frontend
 * (Vite, via alias `@ive/cnpj`) e as edge functions Deno (import relativo
 * `./cnpj.ts` / `../_shared/ive/cnpj.ts`). Mesmo padrão de `@ive/calculator`.
 *
 * Suporta o CNPJ ALFANUMÉRICO — Instrução Normativa RFB nº 2.229/2024, primeiro
 * número emitido em 31/07/2026. Formato: 14 posições = 12 alfanuméricas
 * [A-Z0-9] (raiz + ordem) + 2 dígitos verificadores numéricos [0-9]. O DV é o
 * Módulo 11 clássico, mas cada caractere vale (código ASCII − 48): dígitos
 * '0'..'9' → 0..9; letras 'A'..'Z' → 17..42.
 *
 * RETROCOMPATÍVEL por construção: para um CNPJ 100% numérico, (ASCII−48) de
 * cada dígito é o próprio dígito, então este validador reduz ao algoritmo
 * clássico e aceita TODOS os CNPJs numéricos legados sem alteração. Conferido
 * contra CNPJs públicos reais (Banco do Brasil, Petrobras, Itaú, Correios) e
 * contra vetores alfanuméricos de referência do SERPRO.
 *
 * IMPORTANTE: este arquivo é SEM imports de propósito (Deno-safe + Vite-safe).
 * Não adicionar `Deno.*` nem imports de URL aqui.
 */

// Pesos do Módulo 11 (2..9 cíclicos, aplicados esq→dir sobre as 12/13 posições).
const WEIGHTS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
const WEIGHTS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

// 12 posições alfanuméricas + 2 dígitos verificadores estritamente numéricos.
const CNPJ_ALNUM_RE = /^[A-Z0-9]{12}[0-9]{2}$/

// Só a pontuação da máscara (ponto, barra, hífen, espaço). NÃO casa letras.
const CNPJ_PUNCT_RE = /[.\-/\s]/g

/**
 * Remove APENAS a pontuação de máscara e normaliza para maiúsculas, preservando
 * as letras do CNPJ. É o substituto de `replace(/\D/g, "")` nos pontos de CNPJ
 * (aquele apagava as letras). NÃO usar em CPF/telefone — esses continuam
 * numéricos e devem seguir removendo não-dígitos.
 */
export function unmaskCNPJ(value: string): string {
  // String(...) blinda contra valores não-string em runtime (ex.: um número
  // vindo de JSON.parse de respostas de IA/OCR), evitando throw em .replace.
  return String(value ?? "").replace(CNPJ_PUNCT_RE, "").toUpperCase()
}

/** Valor do caractere no cálculo do DV: (código ASCII − 48). */
function charValue(ch: string): number {
  return ch.charCodeAt(0) - 48
}

function checkDigit(base: string, weights: number[]): number {
  let sum = 0
  for (let i = 0; i < base.length; i++) {
    sum += charValue(base[i]) * weights[i]
  }
  const remainder = sum % 11
  return remainder < 2 ? 0 : 11 - remainder
}

/**
 * Calcula os 2 dígitos verificadores a partir das 12 primeiras posições
 * (numéricas ou alfanuméricas). Retorna string de 2 dígitos. Exposto para
 * testes e geração de vetores.
 */
export function cnpjCheckDigits(base12: string): string {
  const b = unmaskCNPJ(base12).slice(0, 12)
  const d1 = checkDigit(b, WEIGHTS_DV1)
  const d2 = checkDigit(b + String(d1), WEIGHTS_DV2)
  return `${d1}${d2}`
}

/**
 * Valida um CNPJ numérico legado OU alfanumérico (novo formato RFB). Aceita com
 * ou sem máscara. Retrocompatível com todos os CNPJs numéricos existentes.
 */
export function isValidCNPJ(cnpj: string): boolean {
  const c = unmaskCNPJ(cnpj)
  if (!CNPJ_ALNUM_RE.test(c)) return false
  // Rejeita 14 caracteres idênticos (inclui o caso 00000000000000, cujo DV
  // fecha em 00 mas é reconhecidamente inválido). Preserva o comportamento
  // legado que já rejeitava dígitos repetidos.
  if (/^(.)\1{13}$/.test(c)) return false
  return cnpjCheckDigits(c.slice(0, 12)) === c.slice(12)
}

/**
 * Formata um CNPJ (numérico ou alfanumérico) na máscara XX.XXX.XXX/XXXX-XX.
 * Comportamento IDÊNTICO ao anterior para entradas numéricas (mesma máscara
 * progressiva); apenas passa a aceitar letras nas 12 primeiras posições e a
 * normalizar para maiúsculas.
 */
export function formatCNPJ(value: string): string {
  const c = unmaskCNPJ(value).slice(0, 14)
  return c
    .replace(/([A-Z0-9]{2})([A-Z0-9])/, "$1.$2")
    .replace(/([A-Z0-9]{3})([A-Z0-9])/, "$1.$2")
    .replace(/([A-Z0-9]{3})([A-Z0-9])/, "$1/$2")
    .replace(/([A-Z0-9]{4})([A-Z0-9]{1,2})$/, "$1-$2")
}

/**
 * Normalização TOLERANTE para ingestão de TEXTO LIVRE não-confiável (Meta Lead
 * Ads, importações), onde o CNPJ pode chegar com lixo em volta (ex.:
 * "cnpj: 12345678000190", espaços/parênteses colados). Tenta a normalização
 * alfanumérica; se não resultar em 14 caracteres, cai no strip de dígitos — um
 * CNPJ numérico legado nunca contém letras. Preserva o comportamento liberal
 * antigo para o numérico e admite o alfanumérico já limpo. Retorna o melhor
 * candidato; o chamador ainda deve validar (length === 14 / isValidCNPJ).
 * NÃO usar em CPF/telefone.
 */
export function unmaskCNPJLoose(value: string): string {
  const alnum = unmaskCNPJ(value)
  if (alnum.length === 14) return alnum
  const digits = String(value ?? "").replace(/\D/g, "")
  return digits.length === 14 ? digits : alnum
}
